import type { DailyEntry, Profile } from '@tally/lib/types';
import { mifflinBMR, stepKcal, KCAL_PER_KG } from '@tally/lib/reconcile';

const mean = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);

/**
 * Back-derive your actual at-rest burn (BMR + ambient NEAT) from logged data.
 *
 *   real_TDEE  = avg_intake − slope(weight, kcal/kg) × 7700
 *   activity   = avg(workout kcal) + avg(step kcal)
 *   learnedBMR = real_TDEE − activity
 *
 * Compare to Mifflin to see how the textbook formula deviates from the user's actual metabolism.
 * Confidence improves with more days of paired weight + intake data.
 */
export function LearnedBMR({
  today,
  recent,
  profile,
  workoutKcalByDate,
}: {
  today: DailyEntry;
  recent: DailyEntry[];
  profile: Profile;
  workoutKcalByDate: Record<string, number>;
}) {
  // ---- Sample sizes ----
  const intakeEntries = recent.filter((e) => e.calories_in != null);
  const weightEntries = recent.filter((e) => e.weight_kg != null);
  const pairedDays = recent.filter((e) => e.calories_in != null && e.weight_kg != null).length;

  // ---- Real TDEE from slope of weight × kcal/kg + avg intake ----
  const avgIntake = mean(intakeEntries.map((e) => e.calories_in as number));
  const weightSlopePerDay = (() => {
    const weights = weightEntries.map((e) => e.weight_kg as number);
    const n = weights.length;
    if (n < 3) return null;
    const xs = weights.map((_, i) => i);
    const xMean = xs.reduce((a, b) => a + b, 0) / n;
    const yMean = weights.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) {
      num += (xs[i] - xMean) * (weights[i] - yMean);
      den += (xs[i] - xMean) ** 2;
    }
    return den ? num / den : null;
  })();
  const realTDEE = avgIntake != null && weightSlopePerDay != null
    ? avgIntake - weightSlopePerDay * KCAL_PER_KG
    : null;

  // Detect "weight changing too fast" — water/glycogen shifts dominate, so 7700 kcal/kg lies.
  // Threshold: anything beyond ~0.5 kg/wk loss/gain probably has a non-fat component large enough
  // to inflate the TDEE estimate by hundreds of kcal/day.
  const weeklyChangeKg = weightSlopePerDay != null ? weightSlopePerDay * 7 : null;
  const tooFastToTrust = weeklyChangeKg != null && Math.abs(weeklyChangeKg) > 0.5;

  // ---- Avg activity over the same window ----
  const dayCount = recent.length;
  const totalWorkout = recent.reduce((s, e) => s + (workoutKcalByDate[e.date] ?? 0), 0);
  const avgWorkout = dayCount ? totalWorkout / dayCount : 0;
  const totalStepKcal = recent.reduce(
    (s, e) => s + (e.steps != null && e.weight_kg != null ? stepKcal(e.steps, e.weight_kg) : 0),
    0,
  );
  const avgStepKcal = dayCount ? totalStepKcal / dayCount : 0;

  // ---- Back-derived BMR (only meaningful when weight is reasonably stable) ----
  const learnedBMR = realTDEE != null && !tooFastToTrust
    ? Math.round(realTDEE - avgWorkout - avgStepKcal)
    : null;

  // ---- Mifflin reference ----
  const mifflinPrediction = today.weight_kg != null
    ? mifflinBMR({
        weight_kg: today.weight_kg,
        height_cm: profile.height_cm,
        age: profile.age,
        sex: profile.sex,
      })
    : null;
  const diffVsMifflin = learnedBMR != null && mifflinPrediction != null ? learnedBMR - mifflinPrediction : null;

  // ---- Confidence ----
  const confidenceLabel = tooFastToTrust
    ? 'stabilizing'
    : pairedDays >= 60 ? 'high confidence'
    : pairedDays >= 30 ? 'stable'
    : pairedDays >= 14 ? 'usable'
    : pairedDays >= 7 ? 'early signal — noisy'
    : 'learning…';
  const confidenceColor = tooFastToTrust
    ? 'text-amber-400'
    : pairedDays >= 30 ? 'text-emerald-400'
    : pairedDays >= 14 ? 'text-stone-300'
    : 'text-amber-400';

  const fmtKcal = (n: number | null) => (n == null ? '—' : Math.round(n).toLocaleString());

  return (
    <section>
      <div className="border border-stone-600 bg-gradient-to-b from-stone-900 to-stone-950 p-8">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-mono">Learned BMR</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono mt-1">
              your body's actual at-rest burn
            </div>
          </div>
          <div className={`text-[10px] uppercase tracking-[0.2em] font-mono ${confidenceColor}`}>
            {pairedDays}d paired data · {confidenceLabel}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-baseline">
          <div>
            <div className="text-6xl font-light tabular-nums text-stone-50">
              {fmtKcal(learnedBMR)}
              <span className="text-base text-stone-500 ml-2 font-mono">kcal/day</span>
            </div>
            <div className="text-[11px] text-stone-500 font-mono mt-2">at-rest burn (BMR + ambient NEAT)</div>
          </div>

          <div className="border-l border-stone-700 pl-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono mb-1">Mifflin formula</div>
            <div className="text-2xl font-light tabular-nums text-stone-300">
              {fmtKcal(mifflinPrediction)}
              <span className="text-xs text-stone-500 ml-1 font-mono">kcal</span>
            </div>
            <div className="text-[11px] text-stone-500 font-mono mt-2">textbook prediction</div>
          </div>

          <div className="border-l border-stone-700 pl-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono mb-1">Difference</div>
            <div className={`text-2xl font-light tabular-nums ${
              diffVsMifflin == null ? 'text-stone-500'
              : Math.abs(diffVsMifflin) < 50 ? 'text-stone-300'
              : diffVsMifflin > 0 ? 'text-emerald-400' : 'text-amber-400'
            }`}>
              {diffVsMifflin == null ? '—' : `${diffVsMifflin > 0 ? '+' : ''}${diffVsMifflin}`}
              <span className="text-xs text-stone-500 ml-1 font-mono">kcal</span>
            </div>
            <div className="text-[11px] text-stone-500 font-mono mt-2">
              {diffVsMifflin == null ? 'needs more data'
                : Math.abs(diffVsMifflin) < 50 ? 'matches the formula'
                : diffVsMifflin > 0 ? 'faster than formula' : 'slower than formula'}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-stone-800 text-xs text-stone-400 leading-relaxed font-serif">
          {tooFastToTrust ? (
            <>
              <span className="text-amber-400">Weight is dropping ~{Math.abs(weeklyChangeKg!).toFixed(1)} kg/week</span> — too fast for a clean BMR estimate.
              The <span className="text-stone-100">7,700 kcal/kg</span> conversion only holds for pure fat tissue. Early-diet losses are
              mostly water + glycogen (~3,000 kcal/kg), so back-deriving TDEE from rapid scale drops over-states it by hundreds of kcal/day.
              The Learned BMR will start computing once your weekly weight change settles below ~0.5 kg — typically 2–4 weeks into a steady deficit.
              Until then, treat <span className="text-stone-100">Mifflin {fmtKcal(mifflinPrediction)}</span> as your BMR placeholder.
            </>
          ) : (
            <>
              This is your <span className="text-stone-100">actual at-rest burn rate</span>, learned from your data — not a formula.
              We back-derive total daily burn from your scale and food log
              ({fmtKcal(realTDEE)} kcal/day TDEE), then subtract measured activity
              (avg {fmtKcal(avgWorkout)} kcal/day workouts{avgStepKcal > 0 && <>, {fmtKcal(avgStepKcal)} kcal/day steps</>}).
              What's left is your BMR plus small ambient NEAT.
              {pairedDays < 14 && <> The estimate gets sharper with more days of paired weight + intake data — keep logging.</>}
              {pairedDays >= 14 && pairedDays < 30 && <> The estimate is usable but will tighten further past 30 days.</>}
              {pairedDays >= 30 && <> The estimate is stable. Re-check it after big body-comp shifts.</>}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
